import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  fetchProducts,
  fetchProductsStart,
  fetchProductsSuccess,
  fetchProductsFailure,
  addProduct,
  addProductStart,
  addProductSuccess,
  addProductFailure,
  editProduct,
  editProductStart,
  editProductSuccess,
  editProductFailure,
  deleteProduct,
  deleteProductStart,
  deleteProductSuccess,
  deleteProductFailure,
  fetchProductById,
  fetchProductStart,
  fetchProductSuccess,
  fetchProductFailure,
} from "../slices/productsSlice";
import { makeRequest } from "../../utils/api";
import { Product, PaginatedProductResponse } from "../../types/pages/products";
import { extractErrorMessage } from "../../utils/helpers";
import { toast } from "react-toastify";

function* fetchProductsSaga(
  action: PayloadAction<{
    page: number;
    rowsPerPage: number;
    sortBy?: string;
    sortOrder?: number;
  }>,
): Generator<any, void, any> {
  try {
    yield put(fetchProductsStart());
    const skip = action.payload.page * action.payload.rowsPerPage;
    const limit = action.payload.rowsPerPage;
    const sortBy = action.payload.sortBy || "created_at";
    const sortOrder =
      action.payload.sortOrder !== undefined ? action.payload.sortOrder : -1;

    const response: PaginatedProductResponse = yield call(
      makeRequest,
      "get",
      `/products/?skip=${skip}&limit=${limit}&sort_by=${sortBy}&sort_order=${sortOrder}`,
    );
    yield put(fetchProductsSuccess(response));
  } catch (error: any) {
    yield put(fetchProductsFailure(extractErrorMessage(error)));
  }
}

function* addProductSaga(
  action: PayloadAction<
    Omit<Product, "_id" | "created_at" | "updated_at" | "organization">
  >,
): Generator<any, void, any> {
  try {
    yield put(addProductStart());
    const response: Product = yield call(
      makeRequest,
      "post",
      "/products/",
      action.payload,
    );
    yield put(addProductSuccess(response));
    toast.success(response.message ?? "Product added successfully!");
  } catch (error: any) {
    yield put(addProductFailure(extractErrorMessage(error)));
  }
}

function* editProductSaga(
  action: PayloadAction<{
    productId: string;
    productData: Partial<
      Omit<Product, "_id" | "created_at" | "updated_at" | "organization">
    >;
  }>,
): Generator<any, void, any> {
  try {
    yield put(editProductStart());
    const response: Product = yield call(
      makeRequest,
      "put",
      `/products/${action.payload.productId}`,
      action.payload.productData,
    );

    yield put(editProductSuccess(response));
    toast.success(response.message ?? "Product updated successfully!");
  } catch (error: any) {
    yield put(editProductFailure(extractErrorMessage(error)));
  }
}

function* deleteProductSaga(
  action: PayloadAction<string>,
): Generator<any, void, any> {
  try {
    yield put(deleteProductStart());
    yield call(makeRequest, "delete", `/products/${action.payload}`);
    yield put(deleteProductSuccess(action.payload));
    toast.success("Product deleted successfully!");
  } catch (error: any) {
    yield put(deleteProductFailure(extractErrorMessage(error)));
  }
}

function* fetchProductByIdSaga(
  action: PayloadAction<string>,
): Generator<any, void, any> {
  try {
    yield put(fetchProductStart());
    const response: Product = yield call(
      makeRequest,
      "get",
      `/products/${action.payload}`,
    );
    yield put(fetchProductSuccess(response));
  } catch (error: any) {
    yield put(fetchProductFailure(extractErrorMessage(error)));
  }
}

export default function* productsSaga(): Generator<any, void, any> {
  yield takeLatest(fetchProducts.type, fetchProductsSaga);
  yield takeLatest(addProduct.type, addProductSaga);
  yield takeLatest(editProduct.type, editProductSaga);
  yield takeLatest(deleteProduct.type, deleteProductSaga);
  yield takeLatest(fetchProductById.type, fetchProductByIdSaga);
}
